import { Box, Button, Icon, Paragraph } from '@zillow/constellation-10';
import { IconMessageOutline, IconSparkleOutline } from '@zillow/constellation-icons';
import { Folder, ReadRepliedStatus, RenterProgressStatus } from '../../../types/GraphqlTypes';
import useInboxRouterHelper from '../../../hooks/useInboxRouterHelper';
import StringConstants from '../../../constants/StringConstants';

const NO_CONVERSATIONS_TEXT = 'This folder is empty.';
const NO_CONVERSATIONS_DEFAULT = 'When a renter messages you about a property, it will show up here.';
const HAS_MORE_THAN_5_LISTINGS_OVER_90_DAYS_CONVERSATION =
  'To get older conversations, try selecting a single property.';
const OVERDUE_MESSAGES_EMPTY_TEXT = "You're all caught up. Well done!";
const FOLDER_TEXT_OPTIONS = {
  [Folder.Archived]: 'You can move old messages out of your inbox and into your archive.',
  [Folder.Spam]: 'Clear reported messages from your inbox.',
  [Folder.Favorite]: 'You have no conversations marked as starred.',
  // Need ALL Folder options (that are reachable) in order to use enum value as a key
  [Folder.ShowAll]: '',
};

type Props = {
  folder: Folder;
  hasConversationSearchText: boolean;
  hasMoreThan5Listings: boolean;
  isByProperty: boolean;
  readRepliedStatus: ReadRepliedStatus;
  renterProgressStatus: RenterProgressStatus;
  showErrorMessage: boolean;
};

export default function EmptyContainer({
  folder,
  hasConversationSearchText,
  hasMoreThan5Listings,
  isByProperty,
  readRepliedStatus,
  renterProgressStatus,
  showErrorMessage,
}: Props): JSX.Element {
  const { resetFiltersAndFolders } = useInboxRouterHelper();
  let messageCopy: string;
  const hasNoFilters =
    folder === Folder.Inbox &&
    readRepliedStatus === ReadRepliedStatus.ShowAll &&
    renterProgressStatus === RenterProgressStatus.ShowAll &&
    !isByProperty &&
    !hasConversationSearchText;
  const ctaText = hasConversationSearchText ? 'Clear search' : 'Reset filters';

  if (showErrorMessage) {
    messageCopy = StringConstants.GENERIC_ERROR;
  } else if (readRepliedStatus === ReadRepliedStatus.NotRepliedTo) {
    messageCopy = OVERDUE_MESSAGES_EMPTY_TEXT;
  } else if (hasMoreThan5Listings && hasNoFilters) {
    messageCopy = HAS_MORE_THAN_5_LISTINGS_OVER_90_DAYS_CONVERSATION;
  } else if (hasNoFilters) {
    messageCopy = NO_CONVERSATIONS_DEFAULT;
  } else {
    messageCopy = folder !== Folder.Inbox ? FOLDER_TEXT_OPTIONS[folder] : NO_CONVERSATIONS_TEXT;
  }

  return (
    <Box
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        paddingBottom: 'tighter',
        paddingInline: 'default',
        textAlign: 'center',
      }}
    >
      {readRepliedStatus === ReadRepliedStatus.NotRepliedTo ? (
        <Icon css={{ color: 'blue.300', marginBottom: 'default' }} size="lg">
          <IconSparkleOutline />
        </Icon>
      ) : (
        <Box css={{ flexDirection: 'row', display: 'inline-flex' }}>
          <Icon
            size="xl"
            css={{
              transform: 'scaleX(-1)',
              position: 'relative',
              bottom: '20px',
              color: 'blue.300',
            }}
          >
            <IconMessageOutline />
          </Icon>

          <Icon size="xl" css={{ color: 'blue.300', marginBottom: 'default' }}>
            <IconMessageOutline />
          </Icon>
        </Box>
      )}
      <Paragraph css={{ color: 'text.subtle' }}>{messageCopy}</Paragraph>

      {!hasNoFilters && (
        <Button
          emphasis="outlined"
          onClick={resetFiltersAndFolders}
          size="sm"
          tone="brand"
          css={{ marginTop: 'loose' }}
        >
          {ctaText}
        </Button>
      )}
    </Box>
  );
}
